import { preferCodeText } from "@common/helpers/accounting";
import {
    EliminationDetailReportResponse,
    EliminationExceptionJournalEntry,
    EliminationExceptionsReportResponse,
} from "@common/schemas/domains/accounting/elimination.schema";
import { JournalEntrySourceType } from "@common/schemas/domains/accounting/journalEntry";
import {
    EliminationExceptionReason,
    EliminationFactType,
} from "@common/types/domains/accounting/elimination";
import {
    finalizeIntercompanyTransactionRow,
    getDetailOccurrenceKey,
    getDetailSourceEntryKey,
    getExceptionOccurrenceKey,
    getExceptionSourceEntryKey,
} from "./intercompanyTransactionsAggregation.lib";
import type {
    IntercompanyMatchContext,
    IntercompanyTransactionRow,
    IntercompanyTransactionSource,
    IntercompanyTransactionStatus,
} from "./intercompanyTransactions.types";
export type {
    IntercompanyCounterpartAccount,
    IntercompanyMatchContext,
    IntercompanySourceAction,
    IntercompanyTransactionRow,
    IntercompanyTransactionSource,
    IntercompanyTransactionStatus,
} from "./intercompanyTransactions.types";

type DetailRow = EliminationDetailReportResponse["rows"][number];
type ExceptionRow = EliminationExceptionsReportResponse["rows"][number];

export const INTERCOMPANY_TRANSACTION_STATUSES: IntercompanyTransactionStatus[] =
    ["Matched", "Unmatched"];

export const INTERCOMPANY_TRANSACTION_SOURCES: IntercompanyTransactionSource[] =
    [
        "transaction",
        "manual_je",
        "opening_balance",
        "loan",
        "bill",
        "invoice",
        "schedule",
    ];

export const INTERCOMPANY_TRANSACTION_SOURCE_LABELS: Record<
    IntercompanyTransactionSource,
    string
> = {
    transaction: "Transactions",
    manual_je: "Manual journals",
    opening_balance: "Opening balances",
    loan: "Loans",
    bill: "Bills",
    invoice: "Invoices",
    schedule: "Recognition schedules",
};

export function mapJournalEntrySourceToIntercompanyTransactionSource(
    sourceType: JournalEntrySourceType,
): IntercompanyTransactionSource {
    switch (sourceType) {
        case JournalEntrySourceType.Transaction:
            return "transaction";
        case JournalEntrySourceType.ManualJournalEntry:
            return "manual_je";
        case JournalEntrySourceType.OpeningBalance:
            return "opening_balance";
        case JournalEntrySourceType.Loan:
            return "loan";
        case JournalEntrySourceType.Bill:
            return "bill";
        case JournalEntrySourceType.Invoice:
            return "invoice";
        case JournalEntrySourceType.Schedule:
            return "schedule";
        default: {
            const exhaustive: never = sourceType;
            return exhaustive;
        }
    }
}

export function getIntercompanyTransactionSourceLabel(
    source: IntercompanyTransactionSource,
): string {
    switch (source) {
        case "transaction":
            return "Transaction";
        case "manual_je":
            return "Manual journal";
        case "opening_balance":
            return "Opening balance";
        case "loan":
            return "Loan";
        case "bill":
            return "Bill";
        case "invoice":
            return "Invoice";
        case "schedule":
            return "Recognition schedule";
        default: {
            const exhaustive: never = source;
            return exhaustive;
        }
    }
}

interface BuildIntercompanyTransactionRowsParams {
    detailRows: DetailRow[];
    exceptionRows: ExceptionRow[];
}

interface FilterAndSortIntercompanyTransactionRowsParams {
    rows: IntercompanyTransactionRow[];
    search?: string;
    statuses: IntercompanyTransactionStatus[];
    entityIds: number[];
    accountIds: string[];
    accountCodes: number[];
    sources: IntercompanyTransactionSource[];
    sort: "date_asc" | "date_desc";
}

export function buildIntercompanyTransactionRows({
    detailRows,
    exceptionRows,
}: BuildIntercompanyTransactionRowsParams): IntercompanyTransactionRow[] {
    const detailRowsByOccurrenceKey = new Map<
        string,
        IntercompanyTransactionRow
    >();

    for (const detailRow of detailRows) {
        if (
            detailRow.factType !== EliminationFactType.icBalanceSheet ||
            detailRow.origin === "manual"
        ) {
            continue;
        }
        const transactionId = getTransactionId(detailRow);
        const journalEntryId =
            detailRow.manualJournalEntryId ??
            (detailRow.source?.type ===
            JournalEntrySourceType.ManualJournalEntry
                ? String(detailRow.source.id)
                : undefined) ??
            detailRow.journalEntries[0]?.journalEntryId;
        if (!transactionId && !journalEntryId) {
            continue;
        }

        const source = getDetailIntercompanySource(detailRow, transactionId);
        const sourceEntryKey = getDetailSourceEntryKey({
            detailRow,
            source,
            transactionId,
            journalEntryId,
        });
        const occurrenceKey = getDetailOccurrenceKey(detailRow);
        const journalEntries = detailRow.journalEntries.map((journalEntry) => ({
            ...journalEntry,
            entityId: detailRow.pair.entityId,
        }));
        if (!detailRowsByOccurrenceKey.has(occurrenceKey)) {
            detailRowsByOccurrenceKey.set(occurrenceKey, {
                id: `detail:${detailRow.id}`,
                sourceEntryKey,
                reconciliationKey: occurrenceKey,
                date: detailRow.date,
                entityId: detailRow.pair.entityId,
                entityName: detailRow.entityName,
                otherEntityId: detailRow.pair.otherEntityId,
                otherEntityName: detailRow.otherEntityName,
                accountId: detailRow.accountId,
                accountCode: detailRow.accountCode,
                accountLabel: getAccountLabel(detailRow),
                counterpartAccounts: getOffsetAccounts(journalEntries),
                counterpartAmount: 0,
                intercompanyAccountIds: [detailRow.accountId],
                intercompanyAccountCodes:
                    detailRow.accountCode !== null
                        ? [detailRow.accountCode]
                        : [],
                intercompanyAmount: detailRow.amount,
                description: detailRow.description.trim(),
                memo: detailRow.memo.trim(),
                amount: detailRow.amount,
                status: getDetailStatus(detailRow.matchGroupKey),
                source,
                sourceLabel: getIntercompanyTransactionSourceLabel(source),
                sourceTransactionId: transactionId,
                journalEntries,
                sourceAction:
                    source !== "transaction" && journalEntries.length === 1
                        ? "open_journal_entry"
                        : "none",
                isInformational: false,
                matchGroupKey: detailRow.matchGroupKey,
            });
        }
    }

    const exceptionRowsByOccurrenceKey = new Map<
        string,
        IntercompanyTransactionRow
    >();
    for (const exceptionRow of exceptionRows) {
        if (exceptionRow.factType !== EliminationFactType.icBalanceSheet) {
            continue;
        }
        const sourceTransactionId =
            exceptionRow.source === "transaction" &&
            exceptionRow.transactionIds.length === 1
                ? exceptionRow.transactionIds[0]
                : undefined;
        const journalEntries = exceptionRow.journalEntries;
        const offsetAccountIds = new Set(
            journalEntries.flatMap((journalEntry) =>
                journalEntry.offsetAccounts.map((account) => account.id),
            ),
        );
        const intercompanySides = exceptionRow.sides.filter(
            (side) => !offsetAccountIds.has(side.accountId),
        );
        const sourceSides =
            intercompanySides.length > 0
                ? intercompanySides
                : exceptionRow.sides;

        for (const [sideIndex, side] of sourceSides.entries()) {
            const entityJournalEntries = journalEntries.filter(
                (journalEntry) => journalEntry.entityId === side.entityId,
            );
            const sourceJournalEntries = getSourceJournalEntries({
                entityJournalEntries,
            });
            for (const journalEntry of sourceJournalEntries) {
                const source = getExceptionIntercompanySource({
                    exceptionRow,
                    journalEntry,
                });
                const isInformational = isInformationalException(exceptionRow);
                const status: IntercompanyTransactionStatus = isInformational
                    ? "Matched"
                    : "Unmatched";
                const rowJournalEntries = journalEntry
                    ? [journalEntry]
                    : journalEntries;
                const sourceEntryKey = getExceptionSourceEntryKey({
                    exceptionRow,
                    side,
                    source,
                    sourceTransactionId,
                    journalEntry,
                });
                const occurrenceKey = getExceptionOccurrenceKey({
                    exceptionRow,
                    side,
                    sideIndex,
                    journalEntry,
                });
                const row: IntercompanyTransactionRow = {
                    id: `exception:${occurrenceKey}`,
                    sourceEntryKey,
                    reconciliationKey: occurrenceKey,
                    date: exceptionRow.date,
                    entityId: side.entityId,
                    entityName: side.entityName,
                    ...getExceptionCounterpart(exceptionRow, side),
                    accountId: side.accountId,
                    accountCode: side.accountCode,
                    accountLabel: getExceptionAccountLabel(side),
                    counterpartAccounts: getOffsetAccounts(rowJournalEntries),
                    counterpartAmount: 0,
                    intercompanyAccountIds: [side.accountId],
                    intercompanyAccountCodes:
                        side.accountCode === null ? [] : [side.accountCode],
                    intercompanyAmount:
                        sourceSides.length === 1
                            ? exceptionRow.netAmount
                            : side.signedAmount,
                    description: exceptionRow.description.trim(),
                    memo: exceptionRow.memo.trim(),
                    amount:
                        sourceSides.length === 1
                            ? exceptionRow.netAmount
                            : side.signedAmount,
                    status,
                    source,
                    sourceLabel: getIntercompanyTransactionSourceLabel(source),
                    sourceTransactionId,
                    journalEntries: rowJournalEntries,
                    sourceAction: getSourceAction({
                        status,
                        isInformational,
                        sourceTransactionId,
                        source,
                        journalEntry,
                    }),
                    isInformational,
                    matchGroupKey: null,
                };
                if (!exceptionRowsByOccurrenceKey.has(occurrenceKey)) {
                    exceptionRowsByOccurrenceKey.set(occurrenceKey, row);
                }
            }
        }
    }

    const rowsByOccurrenceKey = new Map(detailRowsByOccurrenceKey);
    for (const [occurrenceKey, exceptionRow] of exceptionRowsByOccurrenceKey) {
        if (!rowsByOccurrenceKey.has(occurrenceKey)) {
            rowsByOccurrenceKey.set(occurrenceKey, exceptionRow);
        }
    }

    const occurrenceRows = [...rowsByOccurrenceKey.values()].map(
        finalizeIntercompanyTransactionRow,
    );
    return applyMatchContexts(occurrenceRows).sort(compareNewestFirst);
}

export function parseIntercompanyAccountCode(
    value: string | null,
): number | null {
    if (value === null || value.trim() === "") {
        return null;
    }
    const accountCode = Number(value);
    return Number.isInteger(accountCode) ? accountCode : null;
}

export function parseIntercompanyStatuses(
    values: string[],
): IntercompanyTransactionStatus[] {
    const statuses: IntercompanyTransactionStatus[] = [];
    for (const value of values) {
        let status: IntercompanyTransactionStatus | null = null;
        switch (value) {
            case "Matched":
            case "In transit":
                status = "Matched";
                break;
            case "Unmatched":
                status = "Unmatched";
                break;
            default:
                break;
        }
        if (status !== null && !statuses.includes(status)) {
            statuses.push(status);
        }
    }
    return statuses;
}

export function parseIntercompanySources(
    values: string[],
): IntercompanyTransactionSource[] {
    return values.filter((value): value is IntercompanyTransactionSource =>
        INTERCOMPANY_TRANSACTION_SOURCES.some((source) => source === value),
    );
}

export function parseIntercompanyEntityIds(values: string[]): number[] {
    return values.flatMap((value) => {
        const entityId = Number(value);
        return Number.isSafeInteger(entityId) && entityId > 0 ? [entityId] : [];
    });
}

export function parseIntercompanyAccountCodes(values: string[]): number[] {
    return values.flatMap((value) => {
        const accountCode = parseIntercompanyAccountCode(value);
        return accountCode === null ? [] : [accountCode];
    });
}

export function filterAndSortIntercompanyTransactionRows({
    rows,
    search,
    statuses,
    entityIds,
    accountIds,
    accountCodes,
    sources,
    sort,
}: FilterAndSortIntercompanyTransactionRowsParams): IntercompanyTransactionRow[] {
    const normalizedSearch = search?.trim().toLocaleLowerCase();
    return rows
        .filter((row) => {
            const searchableText = [
                row.entityName,
                row.otherEntityName,
                row.accountLabel,
                row.description,
                row.memo,
                row.sourceLabel,
                row.status,
                row.journalEntries
                    .map(({ referenceNumber }) => referenceNumber)
                    .join(" "),
            ]
                .join(" ")
                .toLocaleLowerCase();
            return (
                (!normalizedSearch ||
                    searchableText.includes(normalizedSearch)) &&
                (statuses.length === 0 || statuses.includes(row.status)) &&
                (entityIds.length === 0 || entityIds.includes(row.entityId)) &&
                (accountIds.length === 0 ||
                    row.counterpartAccounts.some((account) =>
                        accountIds.includes(account.id),
                    )) &&
                (accountCodes.length === 0 ||
                    accountCodes.some((accountCode) =>
                        row.intercompanyAccountCodes.includes(accountCode),
                    ) ||
                    row.counterpartAccounts.some(
                        (account) =>
                            account.code !== null &&
                            accountCodes.includes(account.code),
                    )) &&
                (sources.length === 0 || sources.includes(row.source))
            );
        })
        .sort(sort === "date_desc" ? compareNewestFirst : compareOldestFirst);
}

export function countIntercompanyTransactionRows(
    rows: IntercompanyTransactionRow[],
): Record<IntercompanyTransactionStatus, number> {
    const counts: Record<IntercompanyTransactionStatus, number> = {
        Matched: 0,
        Unmatched: 0,
    };
    for (const row of rows) {
        counts[row.status] += 1;
    }
    return counts;
}

function getTransactionId(row: DetailRow): number | undefined {
    if (row.source?.type !== JournalEntrySourceType.Transaction) {
        return undefined;
    }
    if (typeof row.source.id === "number") {
        return Number.isInteger(row.source.id) ? row.source.id : undefined;
    }
    const parsedId = Number(row.source.id);
    return Number.isInteger(parsedId) ? parsedId : undefined;
}

function getDetailIntercompanySource(
    detailRow: DetailRow,
    transactionId: number | undefined,
): IntercompanyTransactionSource {
    if (detailRow.source?.type !== undefined) {
        return mapJournalEntrySourceToIntercompanyTransactionSource(
            detailRow.source.type,
        );
    }
    return transactionId === undefined ? "manual_je" : "transaction";
}

function getExceptionCounterpart(
    exceptionRow: ExceptionRow,
    side: ExceptionRow["sides"][number],
): { otherEntityId: number; otherEntityName: string } {
    if (side.entityId === exceptionRow.pair.entityId) {
        return {
            otherEntityId: exceptionRow.pair.otherEntityId,
            otherEntityName: exceptionRow.otherEntityName,
        };
    }
    return {
        otherEntityId: exceptionRow.pair.entityId,
        otherEntityName: exceptionRow.entityName,
    };
}

function isInformationalException(exceptionRow: ExceptionRow): boolean {
    return (
        exceptionRow.reason === EliminationExceptionReason.inTransitTransfer ||
        exceptionRow.isInformational
    );
}

function getDetailStatus(
    matchGroupKey: string | null,
): IntercompanyTransactionStatus {
    return matchGroupKey ? "Matched" : "Unmatched";
}

function getExceptionIntercompanySource({
    exceptionRow,
    journalEntry,
}: {
    exceptionRow: ExceptionRow;
    journalEntry: EliminationExceptionJournalEntry | null;
}): IntercompanyTransactionSource {
    if (journalEntry?.source?.type !== undefined) {
        return mapJournalEntrySourceToIntercompanyTransactionSource(
            journalEntry.source.type,
        );
    }
    return exceptionRow.source;
}

function getSourceJournalEntries({
    entityJournalEntries,
}: {
    entityJournalEntries: EliminationExceptionJournalEntry[];
}): Array<EliminationExceptionJournalEntry | null> {
    if (entityJournalEntries.length > 0) {
        return entityJournalEntries;
    }
    return [null];
}

function getSourceAction({
    status,
    isInformational,
    sourceTransactionId,
    source,
    journalEntry,
}: {
    status: IntercompanyTransactionStatus;
    isInformational: boolean;
    sourceTransactionId: number | undefined;
    source: IntercompanyTransactionSource;
    journalEntry: EliminationExceptionJournalEntry | null;
}): IntercompanyTransactionRow["sourceAction"] {
    if (isInformational) {
        return "none";
    }
    if (status === "Unmatched" && sourceTransactionId !== undefined) {
        return "find_transaction_match";
    }
    if (source !== "transaction" && journalEntry !== null) {
        return "open_journal_entry";
    }
    return "none";
}

function getAccountLabel(row: DetailRow): string {
    const code = preferCodeText(row.accountCodeText, row.accountCode);
    return code === null ? row.accountName : `${code} · ${row.accountName}`;
}

function getExceptionAccountLabel(side: ExceptionRow["sides"][number]): string {
    const code = preferCodeText(side.accountCodeText, side.accountCode);
    return code === null ? side.accountName : `${code} · ${side.accountName}`;
}

function getOffsetAccounts(
    journalEntries: EliminationExceptionJournalEntry[],
): IntercompanyTransactionRow["counterpartAccounts"] {
    return [
        ...new Map(
            journalEntries
                .flatMap((journalEntry) => journalEntry.offsetAccounts)
                .map((account) => {
                    const code = preferCodeText(account.codeText, account.code);
                    return [
                        account.id,
                        {
                            id: account.id,
                            code: account.code,
                            codeText: account.codeText,
                            label:
                                code === null
                                    ? account.name
                                    : `${code} · ${account.name}`,
                        },
                    ];
                }),
        ).values(),
    ];
}

function applyMatchContexts(
    rows: IntercompanyTransactionRow[],
): IntercompanyTransactionRow[] {
    const rowsByMatchGroup = new Map<string, IntercompanyTransactionRow[]>();
    for (const row of rows) {
        if (!row.matchGroupKey) {
            continue;
        }
        const groupRows = rowsByMatchGroup.get(row.matchGroupKey) ?? [];
        groupRows.push(row);
        rowsByMatchGroup.set(row.matchGroupKey, groupRows);
    }

    return rows.map((row) => {
        if (!row.matchGroupKey) {
            return row;
        }
        const counterparts = (
            rowsByMatchGroup.get(row.matchGroupKey) ?? []
        ).filter((candidate) => candidate.id !== row.id);
        const hasAmbiguousCounterparts =
            new Set(counterparts.map((candidate) => candidate.entityId)).size >
            1;
        if (hasAmbiguousCounterparts) {
            return {
                ...row,
                matchContext: undefined,
            };
        }
        const [counterpart] = counterparts;
        if (!counterpart) {
            return row;
        }
        return {
            ...row,
            matchContext: buildMatchContext(counterpart),
        };
    });
}

function buildMatchContext(
    counterpart: IntercompanyTransactionRow,
): IntercompanyMatchContext {
    const [journalEntry] = counterpart.journalEntries;
    return {
        entityId: counterpart.entityId,
        entityName: counterpart.entityName,
        accountLabel: counterpart.accountLabel,
        reference: journalEntry?.referenceNumber ?? "",
        date: counterpart.date,
        amount: counterpart.amount,
        journalEntryCount: counterpart.journalEntries.length,
        accountingLedgerId: journalEntry?.accountingLedgerId,
    };
}

function compareNewestFirst(
    firstRow: IntercompanyTransactionRow,
    secondRow: IntercompanyTransactionRow,
): number {
    return (
        (secondRow.date ?? "").localeCompare(firstRow.date ?? "") ||
        firstRow.entityName.localeCompare(secondRow.entityName) ||
        firstRow.id.localeCompare(secondRow.id)
    );
}

function compareOldestFirst(
    firstRow: IntercompanyTransactionRow,
    secondRow: IntercompanyTransactionRow,
): number {
    return (
        (firstRow.date ?? "").localeCompare(secondRow.date ?? "") ||
        firstRow.entityName.localeCompare(secondRow.entityName) ||
        firstRow.id.localeCompare(secondRow.id)
    );
}
